

import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

// @ts-ignore
import draggable from "vuedraggable"

import DataTable from "../DataTable.vue"
import DataForm from "../DataForm.vue"
import FieldSelector from "../Fields/FieldSelector.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import TemplateActions from "./TemplateActions.vue"
import TemplateRules from "./TemplateRules.vue"
import TemplateCadence from "./TemplateCadence.vue"
import DefinitionTitle from "../Definitions/DefinitionTitle.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import FieldControl from "@/components/Fields/FieldControl.vue"
import AddSection from "@/components/Config/AddSection.vue"

import UserImage from "../../UserImage.vue"

import User from "@/types/user"
import { Template, TemplateDetails, Field, Severity, FieldInstance, CaseAction, CaseRule, Team } from "@/types"
import { CaseField } from "@/types/caseField"

import api from "@/services/api"
import apiSeverity from "@/services/api_Severity"

import { LayoutLocations, LayoutLocationsLabels } from "@/types/templateLayout"
import { SectionDetails } from "@/types/templateDetails"
import TeamPermissionsSelector from "@/components/Config/Permissions/TeamPermissionsSelector.vue"
import TemplateScheduleSetup from "./TemplateScheduling.vue"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"data-form": DataForm,
		"field-select": FieldSelector,
		"field-permissions": FieldPermissions,
		"user-image": UserImage,
		"template-actions": TemplateActions,
		"template-rules": TemplateRules,
		"template-scheduling": TemplateScheduleSetup,
		"template-cadence": TemplateCadence,
		draggable,
		"skeleton": SkeletonLoader,
		"definition-title": DefinitionTitle,
		"field-control": FieldControl,
		"team-permissions-selector": TeamPermissionsSelector,
		"add-section": AddSection
	}
})
export default class TemplateSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter("getMe")
	public me!: User;

	@Action public refreshUserInfo!: () => void;

	private severities: Severity[] = [];
	private severitiesDict: any = null;

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Getter("getDataSourcesById")
	dataSourceDictionary!: { [key: string]: any };

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.State
	fields!: Field[];

	@setup.Action loadFields!: () => Promise<any[]>;

	@Getter("getTenantSettings") getTenantSettings!: { [ key: string]: any }

	private layoutLocationsLabels = LayoutLocationsLabels

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				filterable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "description",
				label: "Description",
				sortable: true,
				filterable: true
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "actions",
				inModal: (item: any) => false,
				inTable: false,
				errors: (item: any) => item.actions && item.actions.some((action: CaseAction) => !action.title)
					? "No title entered for action"
					: item.rules && item.rules.some((r: CaseRule) => !r.id) ? "Please select a rule" : null
			}
		]
	}

	public get additionalColumns (): any[] {
		return [
			{
				key: "inheritedFromTitle",
				label: "Inherited From",
				dataType: "list",
				valueColumn: "inheritedFrom",
				options: this.inheritableTemplates,
				sortable: true,
				inTable: false,
				tooltip: "Use this to pull in information from another template. For instance, you may have a general template known as “Facilities Issues” which requires a basic set of information (People involved, Dates of occurrence). From there, you could make an “Elevator Entrapment” Template that inherits from the “Facilities Issue” template. This way, only a handful of additional Fields need to be added, e.g. an elevator identification number."
			},
			{
				key: "allowFromApi",
				label: "Creatable from API",
				dataType: "checkbox",
				valueColumn: "allowFromApi",
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "Enables items of this template to be created via the Cases API. This needs to be enabled in order to raise events from Enterprise or Operations into Cases."
			},
			{
				key: "preventManualCreation",
				label: "Prevent Manual Creation",
				dataType: "checkbox",
				valueColumn: "preventManualCreation",
				inModal: (item: any) => true,
				inTable: false,
				tooltip: `Disables users from being able to manually create ${this.getRecordName === this.getCaseName ? this.getRecordName + "s" : this.getRecordName + "s/" + this.getCaseName + "s"} for this template, however they can still be created automatically via Schedules or Cadence.`
			},
			{
				key: "severityId",
				label: "Default Severity Level",
				dataType: "list",
				valueColumn: "severityId",
				options: this.severities,
				inModal: (item: any) => true,
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				inTable: false,
				tooltip: "Initial severity level when records are created"
			},
			{
				key: "caseNumberFormat",
				label: "Case Number Template",
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				dynamicComponent: DefinitionTitle,
				componentProperties: {
					label: "",
					description: "Format for case number. Leave blank to use simple case number",
					fields: [
						{
							field: {
								id: "caseNumber",
								title: "Case Number"
							}
						},
						{
							field: {
								id: "month",
								title: "Month"
							}
						},
						{
							field: {
								id: "year",
								title: "Year"
							}
						}
					],
					requiredFields: ["caseNumber"],
					allowEmpty: true
				},
				tooltip: "By setting a case number format on a template, you can customize how records with that template will get case numbers. By default, case numbers will be a simple increasing number (1, 2, 3). With a format, you can mix in text, as well as years and months, as well as having a fixed length on the case number itself (eg, SV-2021-00001)",
				errors: (item: any) => item.caseNumberFormat !== "" && item.caseNumberFormat && item.caseNumberFormat.indexOf("{caseNumber}") === -1
					? "Must include case number"
					: null
			},
			{
				key: "caseNumberFormatLength",
				label: "Case Number Length",
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				dynamicComponent: Numeric,
				componentProperties: {
					precision: 0
				},
				tooltip: "Provide a number to specify the length of the case numbers associated with this template."
			},
			{
				key: "hiddenFields",
				label: "Hide Built-in Fields",
				dataType: "list-multiple",
				valueColumn: "hiddenFields",
				options: this.builtInFields,
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "Sometimes it may not be applicable to use all of the built in fields for a case, therefore it is possible to hide these fields from records and cases."
			},
			{
				key: "alwaysPromoted",
				label: "Always Promoted",
				dataType: "checkbox",
				valueColumn: "alwaysPromoted",
				inModal: (item: any) : boolean => {
					const tenantSettings = this.getTenantSettings
					const hideAlwaysPromoteOption = !!tenantSettings && tenantSettings["allTemplatesAlwaysPromoted"]
					return !hideAlwaysPromoteOption
				},
				inTable: false,
				tooltip: `Automatically promotes ${this.getRecordName}s to ${this.getCaseName}s when they are created. If checked, this will also promote any ${this.getRecordName}s created with Scheduling or Cadence Scheduling.`
			},
			{
				key: "enableTitleTemplate",
				label: "Title Template",
				dataType: "checkbox",
				valueColumn: "enableTitleTemplate",
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "The title template is a setting on the Template Setup view which allows for an operator to pre-define an auto-generated title based on the certain field types that belong to a template. Once set a user will be unable to manually alter the title, and can only do so by editing the selected field values."
			}
		]
	}

	public get fieldColumns (): any[] {
		return [
			{
				key: "handle",
				label: ""
			},
			{
				key: "field.title",
				label: "Title"
			},
			{
				key: "field.dataTypeTitle",
				label: "Data Type"
			},
			{
				key: "field.dataSourceTitle",
				label: "Data Source"
			},
			{
				key: "field.allowFiles",
				label: "Allow Files"
			},
			{
				key: "requiredRecord",
				label: "Required on Record"
			},
			{
				key: "permissions.record",
				label: "Record Permissions"
			},
			{
				key: "requiredCase",
				label: "Required on Case"
			},
			{
				key: "permissions.case",
				label: "Case Permissions"
			},
			{
				key: "remove",
				label: ""
			}
		]
	}

	public get builtInFields (): any[] {
		return [
			{
				id: "summary",
				title: "Summary"
			},
			{
				id: "status",
				title: "Status"
			},
			{
				id: "severity",
				title: "Severity"
			},
			{
				id: "affectedAreas",
				title: "Affected Areas"
			},
			{
				id: "links",
				title: "Links"
			},
			{
				id: "tags",
				title: "Tags"
			},
			{
				id: "attachments",
				title: "Attachments"
			}
		]
	}

	public templateDetails: TemplateDetails | null = null;
	public templates: Template[] = [];

	loading: boolean = true;
	showRemoveFieldMessage: boolean = false;

	public inheritedFromLoaded: string | null = null;
	public fieldFilter: string = "";

	public addFieldToSection: string | null = null;

	private fieldsToRemove: FieldInstance[] = [];

	public get inheritFrom () {
		return this.templateDetails ? this.templateDetails.inheritedFrom : null
	}

	public get inheritableTemplates (): Template[] {
		if (!this.templateDetails || !this.templateDetails.id) {
			return this.templates
		}

		const inheritanceChain = new Set<string>()
		inheritanceChain.add(this.templateDetails.id)

		const addInherited = (parent: Template) => {
			this.templates.filter(child => child.inheritedFrom === parent.id).forEach(child => {
				if (child.id) {
					inheritanceChain.add(child.id)
					addInherited(child)
				}
			})
		}

		addInherited(this.templateDetails)

		return this.templates.filter(template => template.id && !inheritanceChain.has(template.id))
	}

	@Watch("inheritFrom")
	public async updateInherited (value: string, oldValue: string) {
		if (this.templateDetails && value !== this.inheritedFromLoaded) {
			if (value) {
				const inheritedTemplate = await api.templateDetails(value)

				this.templateDetails.severityId = inheritedTemplate.severityId
				this.templateDetails.severityTitle = inheritedTemplate.severityTitle
				this.templateDetails.hiddenFields = inheritedTemplate.hiddenFields
				this.templateDetails.caseNumberFormat = inheritedTemplate.caseNumberFormat
				this.templateDetails.caseNumberFormatLength = inheritedTemplate.caseNumberFormatLength
				this.templateDetails.enableTitleTemplate = inheritedTemplate.enableTitleTemplate
				this.templateDetails.titleTemplate = inheritedTemplate.titleTemplate

				this.templateDetails.permissions = inheritedTemplate.permissions
				this.templateDetails.editClosedPermissions = inheritedTemplate.editClosedPermissions

				this.templateDetails.fields.removeWhere((field: FieldInstance) =>
					field.inheritedFrom != null && !inheritedTemplate.fields.some(
						(inheritedField: FieldInstance) => inheritedField.field.id === field.field.id))

				inheritedTemplate.fields.forEach((field: FieldInstance) => {
					if (!this.templateDetails!.fields.some((existingField: FieldInstance) => existingField.field.id === field.field.id)) {
						if (!field.inheritedFrom) {
							field.inheritedFrom = inheritedTemplate.id!
							field.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.fields.push(field)
					}
				})

				this.templateDetails.actions.removeWhere((action: CaseAction) =>
					action.inheritedFrom != null && !inheritedTemplate.actions.some(
						(inheritedAction: CaseAction) => inheritedAction.id === action.id))

				inheritedTemplate.actions.forEach((action: CaseAction) => {
					if (!this.templateDetails!.actions.some((existingAction: CaseAction) => existingAction.id === action.id)) {
						if (!action.inheritedFrom) {
							action.inheritedFrom = inheritedTemplate.id!
							action.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.actions.push(action)
					}
				})

				this.templateDetails.rules.removeWhere((rule: CaseRule) =>
					rule.inheritedFrom != null && !inheritedTemplate.rules.some(
						(inheritedRule: CaseRule) => inheritedRule.id === rule.id))

				inheritedTemplate.rules.forEach((rule: CaseRule) => {
					if (!this.templateDetails!.rules.some((existingRule: CaseRule) => existingRule.id === rule.id)) {
						if (!rule.inheritedFrom) {
							rule.inheritedFrom = inheritedTemplate.id!
							rule.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.rules.push(rule)
					}
				})

				this.templateDetails.sections.removeWhere(section => {
					if (this.templateDetails!.sectionDetails[section].inheritedFrom != null &&
						!inheritedTemplate.sections.some(inheritedSection => inheritedSection === section)) {
						delete this.templateDetails!.sectionDetails[section]
						return true
					}
					return false
				})

				inheritedTemplate.sections.forEach((section) => {
					if (!this.templateDetails!.sections.some((existingSection) => existingSection === section)) {
						var sectionDetail = inheritedTemplate!.sectionDetails[section]
						if (sectionDetail && !sectionDetail.inheritedFrom) {
							sectionDetail.inheritedFrom = inheritedTemplate.id!
							sectionDetail.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.sections.push(section)
						this.templateDetails!.sectionDetails[section] = inheritedTemplate!.sectionDetails[section]
					}
				})

				if (this.templateDetails.inheritedDefaults && this.templateDetails.fieldDefaultValues) {
					Object.keys(this.templateDetails.inheritedDefaults).forEach(fieldId => {
						Vue.delete(this.templateDetails!.fieldDefaultValues as any, fieldId)
					})
				}

				Vue.set(this.templateDetails, "inheritedDefaults", inheritedTemplate.fieldDefaultValues)
				if (inheritedTemplate.fieldDefaultValues) {
					if (!this.templateDetails.fieldDefaultValues) {
						Vue.set(this.templateDetails, "fieldDefaultValues", {})
					}

					Object.keys(inheritedTemplate.fieldDefaultValues).forEach(fieldId => {
						Vue.set(this.templateDetails!.fieldDefaultValues as any, fieldId, inheritedTemplate.fieldDefaultValues![fieldId])
					})
				}
			} else {
				if (this.templateDetails.inheritedDefaults) {
					this.templateDetails.inheritedDefaults = null
				}
				this.templateDetails.fields.removeWhere((field: FieldInstance) => field.inheritedFrom != null)
				this.templateDetails.actions.removeWhere((action: CaseAction) => action.inheritedFrom != null)
				this.templateDetails.rules.removeWhere((rule: CaseRule) => rule.inheritedFrom != null)

				this.templateDetails.sections.removeWhere(section => {
					if (this.templateDetails!.sectionDetails[section].inheritedFrom != null) {
						delete this.templateDetails!.sectionDetails[section]
						return true
					}
					return false
				})
			}

			this.inheritedFromLoaded = this.templateDetails.inheritedFrom
			this.setTemplateDetails(this.templateDetails)
		}
	}

	public async mounted () {
		await this.loadFields()

		this.loadTemplates()
		this.severities = await apiSeverity.severityList()
		this.severitiesDict = this.severities.toDictionary("id")
	}

	async loadTemplates (): Promise<void> {
		this.loading = true
		this.templates = await api.categories()
		this.loading = false
	}

	async loadTemplateDetails (item: Template): Promise<TemplateDetails> {
		return this.setTemplateDetails(await api.templateDetails(item!.id!))
	}

	setTemplateDetails (templateDetails: TemplateDetails) {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		if (templateDetails.inherited) {
			templateDetails.inherited.forEach((inherited: TemplateDetails) => {
				inherited.fields.forEach((inheritedField: FieldInstance) => {
					inheritedField.inheritedFrom = inherited.title
					inheritedField._rowVariant = "muted"

					const templateField = templateDetails.fields.firstOrDefault(templateField =>
						inheritedField.field.id === templateField.field.id)

					if (templateField) {
						templateField.inheritedFrom = inherited.title
						templateField._rowVariant = "muted"
					} else {
						templateDetails.fields.push(inheritedField)
					}
				})
			})
		}

		if (!templateDetails.sections) {
			templateDetails.sections = []
		}

		if (!templateDetails.sectionDetails) {
			templateDetails.sectionDetails = templateDetails.sections.reduce((group, sectionTitle) =>
				({
					...group,
					[sectionTitle]: {
						title: sectionTitle,
						layout: LayoutLocations.Default
					}
				}), {})
		}

		if (!templateDetails.templateSchedule) {
			templateDetails.templateSchedule = {
				schedulingAvailable: false,
				schedulingPermissions: [],
				schedulingFields: [],
				defaultValues: {}
			}
		}

		if (!templateDetails.cadenceDetails) {
			templateDetails.cadenceDetails = {
				cadencePeriod: null,
				cadenceFields: null,
				isCase: false,
				disablePermissions: [],
				disablePermissionsTeams: []
			}
		}
		templateDetails.sectionFields = templateDetails.fields.reduce((group: any, field: FieldInstance) => {
			var section = field.section ? field.section : "General"

			Vue.set(field, "setValue", false)

			field.field.dataTypeTitle = this.fieldTypesById[field.field.dataType!] ? this.fieldTypesById[field.field.dataType!].title : ""

			if (field.field.dataSourceId && this.dataSourceDictionary[field.field.dataSourceId]) {
				field.field.dataSourceTitle = this.dataSourceDictionary[field.field.dataSourceId].title
			}

			const fieldDefinition = this.fields.find(f => f.id === field.field.id)
			if (fieldDefinition) {
				Vue.set(fieldDefinition, "added", true)
			}

			if (templateDetails.sections.indexOf(section) === -1) {
				templateDetails.sections.push(section)
			}

			if (!templateDetails.sectionDetails[section]) {
				templateDetails.sectionDetails[section] = {
					title: section,
					description: null,
					layout: LayoutLocations.Default,
					permissions: { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
				}
			} else if (!templateDetails.sectionDetails[section].permissions) {
				templateDetails.sectionDetails[section].permissions = { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
			}

			(group[section] = group[section] || []).push(field)
			return group
		}, {})

		for (const section in templateDetails.sectionFields) {
			templateDetails.sectionFields[section].sortBy("order")
		}

		templateDetails.actions.sortBy("order")
		templateDetails.rules.sortBy("order")
		return this.templateDetails = templateDetails
	}

	createNewItem () {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		return this.templateDetails = {
			id: null,
			title: "",
			description: "",
			inheritedFrom: null,
			sections: [],
			sectionDetails: {},
			sectionFields: {},
			fields: [],
			actions: [],
			rules: [],
			permissions: {
				case: {
					view: [],
					edit: []
				},
				record: {
					view: [],
					edit: []
				}
			},
			templateSchedule: {
				schedulingAvailable: false,
				schedulingPermissions: [],
				schedulingFields: [],
				defaultValues: {}
			},
			cadenceDetails: {
				cadencePeriod: null,
				cadenceFields: null,
				isCase: false,
				disablePermissions: [],
				disablePermissionsTeams: []
			},
			editClosedPermissions: null
		}
	}

	async save (template: TemplateDetails) {
		if (template.severityId) {
			template.severityTitle = this.severitiesDict[template.severityId].title
		}

		if (template.caseNumberFormat === "") {
			template.caseNumberFormat = null
		}

		for (const section in template.sectionFields) {
			template.sectionFields[section].forEach((field: FieldInstance, index: number) => {
				field.section = section
				field.order = index
			})
		}

		if (template.sections) {
			template.sections.removeWhere(s => template.sectionFields[s] && template.sectionFields[s].length === 0)
		}

		if (template.actions) {
			template.actions.forEach((action: CaseAction, index: number) => {
				action.order = index
			})
		}

		if (template.rules) {
			template.rules.forEach((rule: CaseRule, index: number) => {
				rule.order = index
			})
		}

		// Add assigned teams to teams property
		var cadenceDetails = template.cadenceDetails
		if (cadenceDetails) {
			cadenceDetails.disablePermissions = cadenceDetails.disablePermissionsTeams ? cadenceDetails.disablePermissionsTeams.map((team: any) => {
				if (team.id) {
					return {
						id: team.id,
						title: team.title
					}
				} else {
					const teamDetails = this.teams.find(f => f.id === team)
					if (teamDetails) {
						return {
							id: teamDetails.id,
							title: teamDetails.title
						}
					} else {
						return null
					}
				}
			}).filter((team: any) => team) : []
		}

		template.inherited = null
		await api.templateSave(template)
		this.loadTemplates()

		this.refreshUserInfo()
	}

	async deleteItem (template: TemplateDetails) {
		await api.templateDelete(template)
		this.loadTemplates()
	}

	async addFields (addFields: Field[]) {
		if (!this.templateDetails) {
			return
		}

		if (!this.addFieldToSection) {
			return
		}

		addFields.forEach(field => {
			field.added = true

			if (!this.templateDetails!.fields.some((caseField: FieldInstance) => caseField.field.id === field.id)) {
				const newCaseField = {
					field,
					section: this.addFieldToSection,
					order: 0,
					requiredRecord: false,
					requiredCase: false,
					permissions: {
						case: null,
						record: null
					},
					value: null,
					fromData: null,
					includeInTable: false
				} as FieldInstance

				Vue.set(newCaseField, "setValue", false)

				this.templateDetails!.fields.push(newCaseField)
				if (this.templateDetails!.sections.indexOf(this.addFieldToSection!) === -1) {
					this.templateDetails!.sections.push(this.addFieldToSection!)
				}

				if (!this.templateDetails!.sectionFields[this.addFieldToSection!]) {
					Vue.set(this.templateDetails!.sectionFields, this.addFieldToSection!, [])
				}
				this.templateDetails!.sectionFields[this.addFieldToSection!].push(newCaseField)
			}
		})

		this.addFieldToSection = null
	}

	async removeField (fieldToRemove: FieldInstance) {
		if (!this.templateDetails) {
			return
		}

		this.fieldsToRemove = []
		this.fieldsToRemove.push(fieldToRemove)

		// Search if the field is required by any computed Fields
		this.templateDetails!.fields.filter(f => f.field.extensions && f.field.extensions!.fields).forEach(computedField => {
			if (computedField.field.extensions!.fields.find((id:string) => id === fieldToRemove.field.id)) {
				this.fieldsToRemove.push(computedField)
			}
		})

		// Show remove field warning if multiple fields will be removed
		if (this.fieldsToRemove.length >= 2) {
			this.showRemoveFieldMessage = true
		} else {
			this.removeFields()
		}
	}

	async removeFields () {
		this.fieldsToRemove.forEach(field => {
			const fieldDefinition = this.fields.find(f => f.id === field.field.id)
			if (fieldDefinition) {
				fieldDefinition.added = false
			}

			this.templateDetails!.fields.removeWhere(f => f.field.id === field.field.id)
			this.templateDetails!.sectionFields[field.section!].removeWhere((f: FieldInstance) => f.field.id === field.field.id)
		})

		this.showRemoveFieldMessage = false
		this.fieldsToRemove = []
	}

	async addNewField (fieldId: string) {
		await this.loadFields()
		this.setTemplateDetails(this.templateDetails!)

		const newField = this.fields.firstOrDefault((f: Field) => f.id === fieldId)
		this.addFields([newField])
	}

	createSection (newSectionName: string) {
		if (newSectionName) {
			if (!this.templateDetails!.sectionFields[newSectionName!]) {
				Vue.set(this.templateDetails!.sectionFields, newSectionName!, [])
				this.templateDetails!.sections.push(newSectionName)
			}

			if (!this.templateDetails!.sectionDetails[newSectionName]) {
				Vue.set(this.templateDetails!.sectionDetails, newSectionName, {
					title: newSectionName,
					layout: LayoutLocations.Default,
					permissions: { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
				})
			}
		}
	}

	addAction () {
		if (this.templateDetails) {
			this.templateDetails.actions.push({
				title: "",
				requiredCase: false,
				requiredRecord: false,
				order: 0
			})
		}
	}

	addRule () {
		if (this.templateDetails) {
			this.templateDetails.rules.push({
				id: null,
				title: null,
				runOnCase: false,
				runOnRecord: false,
				order: 0
			})
		}
	}

	public ruleSelected () {
	}

	sectionNameChange (oldValue: string, newValue: string) {
		const changedFields: FieldInstance[] = []

		this.templateDetails!.sectionFields[oldValue].forEach((f: FieldInstance) => {
			if (!f.inheritedFrom) {
				f.section = newValue
				changedFields.push(f)
			}
		})

		if (!this.templateDetails!.sectionFields[newValue]) {
			Vue.set(this.templateDetails!.sectionFields, newValue, [])
		}

		this.templateDetails!.sectionFields[newValue] =
			this.templateDetails!.sectionFields[newValue].concat(changedFields)

		this.templateDetails!.sectionFields[oldValue] = this.templateDetails!.sectionFields[oldValue]
			.filter((f: FieldInstance) => f.section === oldValue)

		if (this.templateDetails!.sections.indexOf(newValue) === -1) {
			this.templateDetails!.sections.push(newValue)
		}

		if (this.templateDetails!.sectionFields[oldValue].length === 0) {
			delete this.templateDetails!.sectionFields[oldValue]
			this.templateDetails!.sections.removeWhere(s => s === oldValue)
		}

		if (!this.templateDetails!.sectionDetails[newValue]) {
			Vue.set(this.templateDetails!.sectionDetails, newValue, this.templateDetails!.sectionDetails[oldValue])
			delete this.templateDetails!.sectionDetails[oldValue]
		}
	}

	public get (item: any, columnKey: string): any {
		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.get(item[topLevel], rest)
		}

		return item[columnKey]
	}

	get sectionDragOptions () {
		return {
			animation: 200,
			group: "sections",
			disabled: false,
			ghostClass: "ghost"
		}
	}
	get fieldDragOptions () {
		return {
			animation: 200,
			group: "fields",
			disabled: false,
			ghostClass: "ghost"
		}
	}
	public getFieldsForTitle (item: any) {
		const allowedFieldType = ["text", "number", "dollar", "date", "date-time", "list", "list-multiple"]
		if (item.fields) {
			let filteredFields = item.fields.filter((fields: any) => allowedFieldType.includes(fields.field.dataType))
			return {
				fields: filteredFields
			}
		} else {
			return {
				fields: null
			}
		}
	}

	private defaultValue (fieldId: string) {
		if (!this.templateDetails || !this.templateDetails.fieldDefaultValues) {
			return null
		}

		const defaultValue = this.templateDetails.fieldDefaultValues[fieldId]
		return defaultValue === undefined ? null : defaultValue
	}

	private defaultValueIsSet (fieldId: string): boolean {
		if (!this.templateDetails || !this.templateDetails.fieldDefaultValues) {
			return false
		}
		return this.templateDetails.fieldDefaultValues[fieldId] !== undefined
	}

	private defaultValueInherited (fieldId: string): boolean {
		return !!this.templateDetails &&
			!!this.templateDetails.inheritedDefaults &&
			this.templateDetails.inheritedDefaults[fieldId] !== undefined
	}

	private defaultValueSet (fieldId: string, value: any) {
		if (!this.templateDetails) {
			return null
		}

		if (!this.templateDetails.fieldDefaultValues) {
			if (value === undefined) {
				return
			}

			Vue.set(this.templateDetails, "fieldDefaultValues", {})
		}

		if (value === undefined) {
			Vue.delete(this.templateDetails.fieldDefaultValues as any, fieldId)
		} else {
			Vue.set(this.templateDetails.fieldDefaultValues as any, fieldId, value)
		}
	}

	private sectionPermissionsSet (section: string): boolean {
		if (!section || !this.templateDetails!.sectionDetails[section]) {
			return false
		}

		var sectionDetail = this.templateDetails!.sectionDetails[section]

		var casePermissionsSet = !!(sectionDetail.permissions.case.view.length > 0 || sectionDetail.permissions.case.edit.length > 0)
		var recordPermissionsSet = !!(sectionDetail.permissions.record.view.length > 0 || sectionDetail.permissions.record.edit.length > 0)
		return casePermissionsSet || recordPermissionsSet
	}

	private sectionPermissionChange (section: string) {
		if (!section || !this.templateDetails!.sectionDetails[section]) {
			return false
		}

		// Reset field permissions when section permissions are unset
		if (!this.sectionPermissionsSet(section)) {
			this.templateDetails!.sectionFields[section].forEach(f => {
				f.permissions.case = null
				f.permissions.record = null
			})
		}
	}

	private get helpText () {
		return `Templates are used as a framework for your ${this.getRecordName}s and ${this.getCaseName}s. ` +
		"They define what types of information should be captured to describe a particular incident. " +
		"Templates consist of a set of Fields, Permissions, Actions, and Rules."
	}
}
